/**
 * Contract frontend untuk API admin Laravel KLIK BKK.
 * Sumber: routes/api.php + App\Support\AdminAccess pada backend.
 *
 * File ini sengaja murni (tanpa React/storage) agar dapat dipakai oleh
 * auth, service, test, dan feature tanpa membuat dependency melingkar.
 */

export const BACKEND_SUPERADMIN_ROLES = Object.freeze([
  "superadmin_app",
  "superadmin",
  "admin",
  "superadmin_pelayanan",
]);

export const ADMIN_ROLE_ALIASES = Object.freeze({
  superadmin: "superadmin_app",
  super_admin: "superadmin_app",
  admin: "superadmin_app",
  superadmin_app: "superadmin_app",
  superadmin_pelayanan: "superadmin_app",

  pelayanan: "registrasi",
  pelayanan_bandara: "registrasi",
  registrasi: "registrasi",
  registration: "registrasi",
  pendaftaran: "registrasi",

  skrining: "skrining",
  screening: "skrining",

  klinik: "klinik",
  clinic: "klinik",
  dokter: "klinik",

  billing: "billing",
  kasir: "billing",
  pembayaran: "billing",

  pembatalan: "pembatalan",
  cancellation: "pembatalan",
});

export function canonicalAdminRole(value) {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_");

  if (!raw) return "";
  return ADMIN_ROLE_ALIASES[raw] || raw;
}

export function isBackendSuperadminRole(value) {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_");
  return BACKEND_SUPERADMIN_ROLES.includes(raw) || canonicalAdminRole(raw) === "superadmin_app";
}

const id = (value) => encodeURIComponent(String(value));

export const ADMIN_ENDPOINTS = Object.freeze({
  dashboard: Object.freeze({
    harian: "/admin/dashboard/harian",
    analitik: "/admin/dashboard/analitik",
    trend: "/admin/dashboard/trend",
    sidebarBadges: "/admin/sidebar-badges",
  }),

  registrasi: Object.freeze({
    list: "/admin/registrasi",
    detail: (registrationId) => `/admin/registrasi/${id(registrationId)}`,
    status: (registrationId) => `/admin/registrasi/${id(registrationId)}/status`,
    mulai: (registrationId) => `/admin/registrasi/${id(registrationId)}/mulai`,
    cancel: (registrationId) => `/admin/registrasi/${id(registrationId)}/cancel`,
    pembatalan: (registrationId) => `/admin/registrasi/${id(registrationId)}/pembatalan`,
    pembatalanBulk: "/admin/registrasi/pembatalan/bulk",
    resendWhatsapp: (registrationId) => `/admin/registrasi/${id(registrationId)}/resend-wa`,
  }),

  skrining: Object.freeze({
    list: "/admin/skrining",
    petugas: "/admin/skrining/petugas",
    mulai: (registrationId) => `/admin/skrining/${id(registrationId)}/mulai`,
    observasi: (registrationId) => `/admin/skrining/${id(registrationId)}/observasi`,
    lanjutVaksinasi: (registrationId) => `/admin/skrining/${id(registrationId)}/lanjut-vaksinasi`,
    notifyKlinik: (registrationId) => `/admin/skrining/${id(registrationId)}/notify-klinik`,
    lanjutKlinik: (registrationId) => `/admin/skrining/${id(registrationId)}/lanjut-klinik`,
    lanjutBilling: (registrationId) => `/admin/skrining/${id(registrationId)}/lanjut-billing`,
    lanjut: (registrationId) => `/admin/skrining/${id(registrationId)}/lanjut`,
    update: (registrationId) => `/admin/skrining/${id(registrationId)}`,
    pembatalan: (registrationId) => `/admin/skrining/${id(registrationId)}/pembatalan`,
    pembatalanBulk: "/admin/skrining/pembatalan/bulk",
  }),

  klinik: Object.freeze({
    list: "/admin/klinik",
    petugas: "/admin/klinik/petugas",
    mulai: (registrationId) => `/admin/klinik/${id(registrationId)}/mulai`,
    save: (registrationId) => `/admin/klinik/${id(registrationId)}/save`,
  }),

  billing: Object.freeze({
    list: "/admin/billing",
    history: "/admin/billing/history",
    services: "/admin/billing/services",
    petugas: "/admin/billing/petugas",
    mulai: (registrationId) => `/admin/billing/${id(registrationId)}/mulai`,
    detail: (registrationId) => `/admin/billing/${id(registrationId)}/detail`,
    update: (registrationId) => `/admin/billing/${id(registrationId)}`,
    status: (registrationId) => `/admin/billing/${id(registrationId)}/status`,
    whatsapp: (registrationId) => `/admin/billing/${id(registrationId)}/whatsapp`,
    whatsappStatus: (registrationId) => `/admin/billing/${id(registrationId)}/whatsapp/status`,
    paymentSave: (registrationId) => `/admin/billing/${id(registrationId)}/payment/save`,
    payment: (registrationId) => `/admin/billing/${id(registrationId)}/payment`,
    paymentResend: (registrationId) => `/admin/billing/${id(registrationId)}/payment/resend`,
    docsSave: (registrationId) => `/admin/billing/${id(registrationId)}/docs/save`,
    docsResend: (registrationId) => `/admin/billing/${id(registrationId)}/docs/resend`,
    docDelete: (registrationId, docId) => `/admin/billing/${id(registrationId)}/docs/${id(docId)}`,
    suratSehatPreview: (registrationId) => `/admin/billing/${id(registrationId)}/surat-sehat/preview`,
    suratSehatGenerate: (registrationId) => `/admin/billing/${id(registrationId)}/surat-sehat/generate`,
    suratSehatSaveData: (registrationId) => `/admin/billing/${id(registrationId)}/surat-sehat/save-data`,
  }),

  pembatalan: Object.freeze({
    list: "/admin/pembatalan",
    restore: (registrationId) => `/admin/pembatalan/${id(registrationId)}/pulihkan`,
  }),

  whatsapp: Object.freeze({
    conversations: "/admin/whatsapp/conversations",
    unreadCount: "/admin/whatsapp/unread-count",
    syncWablas: "/admin/whatsapp/sync-wablas",
    messages: (conversationId) => `/admin/whatsapp/conversations/${id(conversationId)}/messages`,
    retryMessage: (conversationId, messageId) =>
      `/admin/whatsapp/conversations/${id(conversationId)}/messages/${id(messageId)}/retry`,
    markRead: (conversationId) => `/admin/whatsapp/conversations/${id(conversationId)}/read`,
  }),

  staff: Object.freeze({
    list: "/admin/staff",
    detail: (staffId) => `/admin/staff/${id(staffId)}`,
  }),
});
